import { useMemo, useState } from 'react';
import { formatCurrency } from '../../../../common/SharedFunctions';
import { Accordion, BfiDataGrid, Column } from '../../../../common/SharedComponents';
import dayjs from 'dayjs';
import { DrawApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights';
import { DrawType, DrawWaiverActionType, DrawWaiverType, PermissionType } from '@apply/constants';
import { ConfirmModal, hasPermission } from '@apply/ui';
import CheckBox from 'devextreme-react/check-box';
import { RefreshEvents } from '../../../../common/SharedEvents';

export const DrawHistoryGrid = ({ property }) => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const hasPermissionToWaive = useMemo(() => hasPermission(PermissionType.DrawWaiveBalance), []);

    const [isExpanded] = useState(true);
    const [drawHistoryEntries, set_drawHistoryEntries] = useState(property?.drawHistoryLines);
    const [waiverDrawId, set_waiverDrawId] = useState(null);
    const [waiverApplied, set_waiverApplied] = useState(null);
    const [isConfirmWaiverToggleOpen, set_isConfirmWaiverToggleOpen] = useState(false);
    const [oobAccomadationConfirmText, set_oobAccomodationConfirmText] = useState('');

    const removeWaiver = async (drawId) => {
        const response = drawApi.setDrawWaiver({
            drawId: drawId,
            drawWaiverType: DrawWaiverType.OutOfBalanceWaiver,
            drawWaiverActionType: DrawWaiverActionType.OutOfBalanceRemoveWaiver,
            isOutOfBalanceWaived: false,
        });
        if ((await response).statusCode !== 200) {
            return;
        } else {
            const updatedDraws = drawHistoryEntries.map((drawHistoryItem) => {
                if (drawHistoryItem.drawId === drawId) {
                    return { ...drawHistoryItem, isOutOfBalanceWaived: false };
                } else {
                    return { ...drawHistoryItem };
                }
            });

            set_drawHistoryEntries(updatedDraws);
            set_isConfirmWaiverToggleOpen(false);
            refreshEvents.dispatchRefreshEvent();
        }
    };

    const setWaiver = async (drawId) => {
        const response = drawApi.setDrawWaiver({
            drawId: drawId,
            drawWaiverType: DrawWaiverType.OutOfBalanceWaiver,
            drawWaiverActionType: DrawWaiverActionType.OutOfBalanceWaived,
            isOutOfBalanceWaived: true,
        });
        if ((await response).statusCode !== 200) {
            return;
        } else {
            const updatedDraws = drawHistoryEntries.map((drawHistoryItem) => {
                if (drawHistoryItem.drawId === drawId) {
                    return { ...drawHistoryItem, isOutOfBalanceWaived: true };
                } else {
                    return { ...drawHistoryItem };
                }
            });

            set_drawHistoryEntries(updatedDraws);
            set_isConfirmWaiverToggleOpen(false);
            refreshEvents.dispatchRefreshEvent();
        }
    };

    const toggleDrawOOBAccomodationWaiver = () => {
        if (waiverApplied) {
            removeWaiver(waiverDrawId);
        } else {
            setWaiver(waiverDrawId);
        }
    };

    const waivedRender = (e) => {
        if (e.data && e.data?.isBalanceFlagged) {
            set_waiverDrawId(e.data?.drawId);
            set_waiverApplied(e.data?.isOutOfBalanceWaived);
            set_oobAccomodationConfirmText(
                'Are you sure you want to change the ' +
                    (e.data?.drawType === DrawType.Automatic ? 'Accomodation' : 'Out of Balance') +
                    ' status?'
            );

            let titleText = '';
            if (!e.data.isLatestDraw) titleText = 'Only latest draw can be edited.';
            if (!hasPermissionToWaive) titleText = 'You do not have permission to waive the balance.';

            return (
                <div title={titleText}>
                    <CheckBox
                        onValueChanged={() => set_isConfirmWaiverToggleOpen(true)}
                        value={e.data.isOutOfBalanceWaived}
                        readOnly={!e.data.isLatestDraw || !hasPermissionToWaive}
                    />
                </div>
            );
        }
    };

    const renderHistoryHeader = () => {
        if (property?.drawBudgetId) {
            const anyDraws = drawHistoryEntries && drawHistoryEntries.length > 0;

            if (anyDraws) {
                const anyPendingDraw = !drawHistoryEntries[drawHistoryEntries.length - 1].fundedOn;
                if (anyPendingDraw) {
                    return (
                        <p>
                            Equity Requirement is <b>{formatCurrency(property.equityRequirement, 0)}</b> and the borrower
                            requested a draw <b>{daysSinceDrawRequested()} days</b> ago.
                        </p>
                    );
                } else {
                    return (
                        <p>
                            <b>{formatCurrency(property.drawFundedTotal, 0)}</b> funded. No current draw requests.
                        </p>
                    );
                }
            } else {
                if (property?.equityTotal === 0) {
                    return (
                        <p>
                            Equity Requirement is <b>{formatCurrency(property.equityRequirement, 0)}</b>. No draws requested.
                        </p>
                    );
                } else if (property?.equityTotal < property?.equityRequirement) {
                    return (
                        <p>
                            <b>{formatCurrency(property.equityTotal, 0)}</b> of{' '}
                            <b>{formatCurrency(property.equityRequirement, 0)}</b> Equity Requirement met. No draws requested.
                        </p>
                    );
                } else {
                    return <p>Equity Requirement met. No draws requested.</p>;
                }
            }
        }
    };

    const daysSinceDrawRequested = (): number => {
        if (!drawHistoryEntries || drawHistoryEntries.length === 0) return 0;

        const lastDrawHistory = drawHistoryEntries[drawHistoryEntries.length - 1];
        if (!lastDrawHistory || !lastDrawHistory.borrowerRequested) return 0;

        const latestDrawBorrowerRequestedDate = dayjs(lastDrawHistory.borrowerRequested);
        if (!latestDrawBorrowerRequestedDate) return 0;

        const today = dayjs();
        return today.diff(latestDrawBorrowerRequestedDate, 'day');
    };

    return (
        <>
            <div
                className='accordion'
                data-accordion=''
                data-allow-all-closed='true'
                data-multi-expand='true'
            >
                <Accordion
                    title={'History'}
                    isExpanded={isExpanded}
                    isCollapsable={true}
                >
                    {renderHistoryHeader()}
                    <BfiDataGrid
                        dataSource={drawHistoryEntries}
                        applicationInsights={applicationInsights}
                        defaultPageSize={0}
                        wordWrapEnabled={true}
                        keyExpr='drawNumber'
                        remoteOperations={false}
                    >
                        <Column
                            calculateCellValue={(cellData) => cellData.drawNumber + 1}
                            alignment={'center'}
                            caption='Draw'
                            width='60px'
                        />
                        <Column
                            dataField='amountFunded'
                            caption='Amount'
                            format={'currency'}
                        />
                        <Column
                            dataField='borrowerRequested'
                            alignment={'center'}
                            caption='Requested'
                        />
                        <Column
                            dataField='fundedOn'
                            alignment={'center'}
                            caption='Funded'
                        />
                        <Column
                            dataField='inspectionOrdered'
                            alignment={'center'}
                            caption='Inspection Ordered'
                        />
                        <Column
                            dataField='inspectedOn'
                            alignment={'center'}
                            caption='Inspection Complete'
                        />
                        <Column
                            dataField='outOfBalanceAccomodationNotes'
                            alignment={'center'}
                            caption='OOB / Acc'
                        />
                        <Column
                            cellRender={waivedRender}
                            alignment={'center'}
                            caption='Waived'
                        />
                    </BfiDataGrid>
                </Accordion>
            </div>
            <ConfirmModal
                isOpen={isConfirmWaiverToggleOpen}
                text={oobAccomadationConfirmText}
                confirmCallback={toggleDrawOOBAccomodationWaiver}
                cancelCallback={() => set_isConfirmWaiverToggleOpen(false)}
            />
        </>
    );
};
